import { Link, Navigate, useLocation } from 'react-router-dom';
import { useMe } from '../lib/auth.js';
import { useLicenses, useRenew, shortBinding, shortDate, type LicenseListItem } from '../lib/dashboard.js';

export function LicensesPage(): JSX.Element {
  const me = useMe();
  const location = useLocation();
  const q = useLicenses();
  const renew = useRenew();

  if (me.isLoading) return <p className="text-sm text-slate-500">Loading...</p>;
  if (!me.data) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (q.isLoading) return <p className="text-sm text-slate-500">Loading licenses...</p>;
  if (q.isError) {
    return (
      <div className="rounded bg-red-50 p-4 text-sm text-red-700" data-testid="licenses-error">
        Could not load: {(q.error as Error).message}
      </div>
    );
  }
  const licenses = q.data ?? [];
  if (licenses.length === 0) {
    return (
      <div data-testid="licenses-empty" className="rounded bg-slate-100 p-6 text-sm text-slate-700">
        No licenses yet.{' '}
        <Link to="/" className="font-medium text-slate-900 underline">
          Browse the catalog
        </Link>{' '}
        to issue your first one.
      </div>
    );
  }

  const onRenew = async (issuanceId: string) => {
    const r = await renew.mutateAsync([issuanceId]);
    window.location.assign(r.checkoutUrl);
  };

  return (
    <div data-testid="licenses" className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Your licenses</h1>
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2">Product</th>
              <th className="px-3 py-2">Binding</th>
              <th className="px-3 py-2">Deployment notes</th>
              <th className="px-3 py-2">Issued</th>
              <th className="px-3 py-2">Expires</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {licenses.map((l) => (
              <LicenseRow key={l.issuance.issuanceId} item={l} onRenew={onRenew} renewing={renew.isPending} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LicenseRow({
  item,
  onRenew,
  renewing
}: {
  item: LicenseListItem;
  onRenew: (issuanceId: string) => void;
  renewing: boolean;
}): JSX.Element {
  const { issuance, expired, expiringSoon } = item;
  return (
    <tr data-testid={`license-row-${issuance.issuanceId}`}>
      <td className="px-3 py-2 text-slate-700">{issuance.skuId}</td>
      <td className="px-3 py-2 font-mono text-xs text-slate-700">{shortBinding(issuance.bindingValue)}</td>
      <td
        className="max-w-xs truncate px-3 py-2 text-slate-700"
        title={issuance.deploymentNotes ?? ''}
        data-testid={`license-notes-${issuance.issuanceId}`}
      >
        {issuance.deploymentNotes ?? <span className="text-slate-400">--</span>}
      </td>
      <td className="px-3 py-2 text-slate-700">{shortDate(issuance.issuedAt)}</td>
      <td className="px-3 py-2 text-slate-700">{shortDate(issuance.expiresAt)}</td>
      <td className="px-3 py-2">
        <ExpiryBadge expired={expired} expiringSoon={expiringSoon} />
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-2">
          <a
            href={`/api/licenses/${encodeURIComponent(issuance.issuanceId)}/download`}
            className="text-xs text-slate-900 underline hover:text-slate-700"
            data-testid={`license-download-${issuance.issuanceId}`}
            download
          >
            Download
          </a>
          <button
            type="button"
            onClick={() => onRenew(issuance.issuanceId)}
            disabled={renewing}
            className={`rounded px-3 py-1 text-xs font-medium ${
              expired || expiringSoon
                ? 'bg-slate-900 text-white hover:bg-slate-800 disabled:bg-slate-400'
                : 'border border-slate-300 text-slate-900 hover:bg-slate-100 disabled:opacity-50'
            }`}
            data-testid={`license-renew-${issuance.issuanceId}`}
          >
            Renew
          </button>
        </div>
      </td>
    </tr>
  );
}

function ExpiryBadge({ expired, expiringSoon }: { expired: boolean; expiringSoon: boolean }): JSX.Element {
  if (expired) {
    return <span className="inline-block rounded bg-red-50 px-2 py-0.5 text-xs font-medium text-red-800">Expired</span>;
  }
  if (expiringSoon) {
    return <span className="inline-block rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">Expiring soon</span>;
  }
  return <span className="inline-block rounded bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800">Active</span>;
}
